import { inject, Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { ID } from '@core/ngrx-legacy';
import { TellentHttpApiService } from '@core/tellent-http-legacy';
import {
  AssignMembersOptions,
  GeneratePositionDescriptionPayload,
  GeneratePositionDescriptionResponse,
  PositionAssignment,
  PositionHistoryEntryPayload,
  TellentPosition,
  TellentPositionResponse,
  UpdatePositionPayload,
} from '@core/tellent-positions-types';
import { ORG_GUID } from '@core/tellent-types';

@Injectable({
  providedIn: 'root',
})
export class TellentPositionsHttpClient {
  private readonly httpApi = inject(TellentHttpApiService);
  private readonly orgGuid = inject(ORG_GUID);

  // Snake_case on purpose. The `x-json-accent` plug underscores `conn.params` and
  // `conn.body_params`, but OpenApiSpex casts declared query parameters straight from the raw
  // `conn.query_params`, which the plug never rewrites — so `includeDeleted` would not match the
  // declared `include_deleted` and would silently fall back to its `default: false`.
  public fetchPositions(includeDeleted = false): Observable<TellentPosition[]> {
    return this.httpApi
      .get(
        `/positions/org/${this.orgGuid}/positions`,
        includeDeleted ? { include_deleted: true } : null,
        { skipCompany: true },
      )
      .pipe(map(response => response.data));
  }

  public createPosition(name: string): Observable<TellentPosition> {
    return this.httpApi
      .post(`/positions/org/${this.orgGuid}/positions`, null, { name }, { skipCompany: true })
      .pipe(map(response => response.position));
  }

  public fetchPosition(positionGuid: ID | string): Observable<TellentPositionResponse> {
    return this.httpApi
      .get(`/positions/org/${this.orgGuid}/positions/${positionGuid}`, null, { skipCompany: true })
      .pipe(map(response => ({ data: response.position })));
  }

  public updatePosition(
    positionGuid: string,
    position: UpdatePositionPayload,
  ): Observable<TellentPosition> {
    return this.httpApi
      .patch(
        `/positions/org/${this.orgGuid}/positions/${positionGuid}`,
        null,
        { ...position },
        { skipCompany: true },
      )
      .pipe(map(response => response.position));
  }

  public assignMembers(
    positionGuid: string,
    membersGuids: string[],
    options?: AssignMembersOptions,
  ): Observable<TellentPosition> {
    return this.httpApi
      .patch(
        `/positions/org/${this.orgGuid}/positions/${positionGuid}/assign`,
        null,
        { membersGuids, ...options },
        { skipCompany: true },
      )
      .pipe(map(response => response.position));
  }

  // No `startDate`: a correction only restates which position the member holds, so the rewritten
  // row keeps the date it already had. The endpoint accepts no date at all.
  public reassignMembers(
    positionGuid: string,
    membersGuids: string[],
  ): Observable<TellentPosition> {
    return this.httpApi
      .patch(
        `/positions/org/${this.orgGuid}/positions/${positionGuid}/reassign`,
        null,
        { membersGuids },
        { skipCompany: true },
      )
      .pipe(map(response => response.position));
  }

  public unassignMembers(
    positionGuid: string,
    membersGuids: string[],
  ): Observable<TellentPosition> {
    return this.httpApi
      .patch(
        `/positions/org/${this.orgGuid}/positions/${positionGuid}/unassign`,
        null,
        { membersGuids },
        { skipCompany: true },
      )
      .pipe(map(response => response.position));
  }

  public fetchMemberPositionHistory(memberGuid: string): Observable<PositionAssignment[]> {
    return this.httpApi
      .get(`/positions/org/${this.orgGuid}/members/${memberGuid}/position-assignments`, null, {
        skipCompany: true,
      })
      .pipe(map(response => response.data));
  }

  // A full replace, applied in one transaction: an entry with an id rewrites that assignment, an
  // entry without one creates it, and any of the member's assignments missing from the list is
  // deleted.
  public updateMemberPositionHistory(
    memberGuid: string,
    assignments: PositionHistoryEntryPayload[],
  ): Observable<PositionAssignment[]> {
    return this.httpApi
      .patch(
        `/positions/org/${this.orgGuid}/members/${memberGuid}/position-assignments`,
        null,
        { assignments },
        { skipCompany: true },
      )
      .pipe(map(response => response.data));
  }

  public generateDescription(
    payload: GeneratePositionDescriptionPayload,
  ): Observable<GeneratePositionDescriptionResponse> {
    return this.httpApi.post(
      `/positions/org/${this.orgGuid}/positions/description`,
      null,
      payload,
      {
        skipCompany: true,
      },
    );
  }

  public deletePosition(positionGuid: string): Observable<void> {
    return this.httpApi
      .delete(`/positions/org/${this.orgGuid}/positions/${positionGuid}`, null, {
        skipCompany: true,
      })
      .pipe(map(() => undefined));
  }
}
